import { useCallback, useState } from 'react';
import { isColumnGroup } from '../../domain';
import type { ColumnDef, ResolvedColumn } from '../../domain';

function groupKey(id: string | undefined, header: string | undefined): string {
    return id ?? header ?? '';
}

export function collapsibleGroupKeys<T>(defs: ColumnDef<T>[]): Set<string> {
    const keys = new Set<string>();
    defs.forEach((def) => {
        if (
            isColumnGroup(def) &&
            def.collapsible !== false &&
            def.columns.length > 2
        ) {
            keys.add(groupKey(def.id, def.header));
        }
    });
    return keys;
}

export function filterCollapsedColumns<T>(
    columns: ResolvedColumn<T>[],
    isCollapsed: (key: string) => boolean,
): ResolvedColumn<T>[] {
    const out: ResolvedColumn<T>[] = [];
    let runKey = '';
    let runIndex = 0;
    columns.forEach((col) => {
        const key = groupKey(col.groupId, col.groupHeader);
        if (key !== runKey) {
            runKey = key;
            runIndex = 0;
        }
        if (key === '' || !isCollapsed(key) || runIndex < 2) {
            out.push(col);
        }
        runIndex += 1;
    });
    return out;
}

export interface ColumnGroupsState {
    isCollapsed: (key: string) => boolean;
    toggle: (key: string) => void;
}

export function useColumnGroups(): ColumnGroupsState {
    const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set());

    const toggle = useCallback((key: string) => {
        setCollapsed((prev) => {
            const next = new Set(prev);
            if (next.has(key)) {
                next.delete(key);
            } else {
                next.add(key);
            }
            return next;
        });
    }, []);

    const isCollapsed = useCallback(
        (key: string) => collapsed.has(key),
        [collapsed],
    );

    return { isCollapsed, toggle };
}
